import { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import styles from './SetUpPassword.module.scss';
import { Button, CredentialsPageContainer, Input, PasswordRequirements } from 'components';
import { appActions } from 'store/app/slice';
import { authActions } from 'store/auth/slice';
import { useDispatch } from 'store/utils';
import { decodeBase64Object, getQueryParamValue } from 'utils';

const getPasswordBasedOnEnv = () => {
  if (process.env.NODE_ENV === 'production') return window.location.href;

  return window.location.href.replace('http://localhost:3000', 'https://front.urbansmile.app');
};

const schema = yup.object({
  password: yup
    .string()
    .required('The field is empty')
    .matches(/^[A-Za-z0-9!@#$%^&*()_+\-=[\]{}|;:'",.<>?/~]{8,25}$/, {
      message: "The input doesn't match password requirements",
    })
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*()_+\-=[\]{}|;:'",.<>?/~]/, 'Password must contain at least one special character'),

  repeatPassword: yup
    .string()
    .required('The field is empty')
    .oneOf([yup.ref('password')], "Passwords must match The input doesn't match with Password field input."),
});

enum Errors {
  INCORRECT = 'Incorrect username or password.',
  USER_IS_SIGNED = 'There is already a signed in user.',
  NEEDS_AUTH = 'User needs to be authenticated to call this API.',
  USER_EXISTS = 'Your account already has a password. Go to the login page.',
}

export const SetUpPassword = () => {
  const { userId } = useParams();
  const isSent = useRef(false);
  const params = useLocation();
  const navigate = useNavigate();
  const dataParam = getQueryParamValue(params.search, 'token');
  const { name: username, email } = decodeBase64Object(dataParam!);

  const [isInputDisabled, setIsInputDisabled] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm({ resolver: yupResolver(schema) });

  const watchedPassword = watch('password') as string | undefined;

  const dispatch = useDispatch();

  useEffect(() => {
    const signIn = async () => {
      if (isSent.current) return; // not to run 2 times in dev mode
      isSent.current = true;

      const authParams = {
        username: email,
        password: getPasswordBasedOnEnv(),
      };

      try {
        dispatch(appActions.setAppIsLoading(true));
        await dispatch(authActions.initialSignIn(authParams)).unwrap();
      } catch (error: any) {
        if (error?.message === Errors.INCORRECT) return;
        if (error?.message === Errors.USER_IS_SIGNED) {
          setIsInputDisabled(true);
        }
        setError('repeatPassword', { type: '400', message: error?.message });
      } finally {
        dispatch(appActions.setAppIsLoading(false));
      }
    };

    signIn();
  }, []);

  const onSubmit = handleSubmit(async (data) => {
    if (!userId) return;

    try {
      dispatch(appActions.setAppIsLoading(true));
      const { isSuccess } = await dispatch(
        authActions.updatePassword({
          oldPassword: getPasswordBasedOnEnv(),
          newPassword: data.password,
          userId,
        }),
      ).unwrap();

      isSuccess && navigate('/');
    } catch (error: any) {
      if (error?.message === Errors.NEEDS_AUTH) {
        setError('repeatPassword', { type: '400', message: Errors.USER_EXISTS });
        setIsInputDisabled(true);
        return;
      }
      setError('repeatPassword', { type: '400', message: error?.message });
    } finally {
      dispatch(appActions.setAppIsLoading(false));
    }
  });

  return (
    <CredentialsPageContainer formTitle={`Hey, ${username}`} formSubtitle="Please, set up your new password">
      <form className={styles.formContainer}>
        <div className={styles.passwordInputContainer}>
          <div>
            <Input
              inputName="password"
              labelText="Password"
              placeholder="Enter your password"
              register={register}
              registerOptions={{ required: true }}
              canBeHidden
              errorMsg={errors.password?.message}
              disabled={isInputDisabled}
            />
            {!isInputDisabled && <PasswordRequirements currentPassword={watchedPassword} />}
          </div>
          <Input
            inputName="repeatPassword"
            labelText="Repeat password"
            placeholder="Repeat your password"
            errorMsg={errors.repeatPassword?.message}
            register={register}
            registerOptions={{ required: true }}
            canBeHidden
            disabled={isInputDisabled}
          />
        </div>
        {!isInputDisabled && (
          <Button onClick={onSubmit} className={styles.confirmButton}>
            Confirm
          </Button>
        )}
      </form>
    </CredentialsPageContainer>
  );
};
